// T-057: generazione PDF del menù del locale, lato app.
// Costruisce un PDF A4 multi-pagina direttamente con jsPDF a partire
// dalle categorie + items già caricati nella MenuPage — niente HTML→canvas,
// niente font esterni (Helvetica + Times di default in jsPDF). Layout
// tipografico semplice: header con nome locale + tagline + chef, poi
// per ogni categoria heading + lista piatti (nome, prezzo a destra,
// descrizione italica grigia, allergeni piccoli sotto).
//
// Usato da MenuPage.tsx per i bottoni "Condividi Menù (PDF)" /
// "Condividi Aperitivo (PDF)": il PDF generato viene salvato su
// Filesystem.Cache e condiviso via @capacitor/share su native, o
// scaricato come download su web/PWA.

import { parseTakeawayVariant } from '@trackeat/shared';
import { jsPDF } from 'jspdf';

export interface MenuPdfCategory {
  id: string;
  name: string;
  section?: 'food' | 'bar';
}

export interface MenuPdfItem {
  category_id: string;
  name: string;
  description: string | null;
  price_cents: number | null;
  price_unit?: string | null;
  allergens: string[];
  visible: boolean;
  out_of_stock: boolean;
  sort_order: number;
}

export interface MenuPdfInput {
  businessName: string;
  tagline?: string | null;
  chefName?: string | null;
  chefSubtitle?: string | null;
  location?: string | null;
  primaryColor?: string | null;  // #RRGGBB
  accentColor?: string | null;
  categories: MenuPdfCategory[];
  items: MenuPdfItem[];
  target: 'menu' | 'aperitivo' | 'takeaway';
  allergenLabels: Record<string, string>;
}

const APERITIVO_CAT_NAMES = ['aperitivi', 'aperitivo', 'cocktail', 'cocktails'];

function isAperitivoCat(name: string): boolean {
  return APERITIVO_CAT_NAMES.includes(name.toLowerCase());
}

function filterCategoriesForTarget(
  cats: MenuPdfCategory[],
  target: 'menu' | 'aperitivo' | 'takeaway',
): MenuPdfCategory[] {
  if (target === 'takeaway') return cats;
  if (target === 'menu') {
    // Vista "menù completo" sul pubblico = section food + non aperitivo
    return cats.filter((c) => c.section !== 'bar' && !isAperitivoCat(c.name));
  }
  // Vista "aperitivo" = categorie aperitivo + tutto il bar (Vini, Birre, Acque, Amari)
  return cats.filter((c) => isAperitivoCat(c.name) || c.section === 'bar');
}

function hexToRgb(hex?: string | null): [number, number, number] {
  if (!hex) return [29, 56, 69];
  const h = hex.replace(/^#/, '');
  if (!/^[0-9a-f]{6}$/i.test(h)) return [29, 56, 69];
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function formatPrice(cents: number | null): string {
  if (cents == null) return '—';
  const e = cents / 100;
  return e % 1 === 0 ? `€ ${e.toFixed(0)}` : `€ ${e.toFixed(2)}`;
}

export function generateMenuPdf(input: MenuPdfInput): { base64: string; arrayBuffer: ArrayBuffer } {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210;
  const H = 297;
  const M = 16;
  const innerW = W - 2 * M;
  const [ar, ag, ab] = hexToRgb(input.accentColor ?? '#c69b50');
  const [pr, pg, pb] = hexToRgb(input.primaryColor ?? '#2c6e7e');
  const INK: [number, number, number] = [29, 56, 69];

  let y = M;

  // ─── Header del locale ──────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(INK[0], INK[1], INK[2]);
  doc.text(input.businessName, W / 2, y + 6, { align: 'center' });
  y += 12;

  if (input.location) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(ar, ag, ab);
    doc.text(input.location.toUpperCase(), W / 2, y, { align: 'center' });
    y += 5;
  }

  if (input.tagline) {
    doc.setFont('times', 'italic');
    doc.setFontSize(11);
    doc.setTextColor(70, 70, 70);
    const lines = doc.splitTextToSize(input.tagline, innerW * 0.75) as string[];
    lines.forEach((line) => {
      doc.text(line, W / 2, y, { align: 'center' });
      y += 5;
    });
  }

  if (input.chefName || input.chefSubtitle) {
    y += 1;
    doc.setDrawColor(ar, ag, ab);
    doc.setLineWidth(0.2);
    doc.line(W / 2 - 22, y, W / 2 + 22, y);
    y += 5;
    if (input.chefName) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(INK[0], INK[1], INK[2]);
      doc.text(input.chefName, W / 2, y, { align: 'center' });
      y += 4;
    }
    if (input.chefSubtitle) {
      doc.setFont('times', 'italic');
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(input.chefSubtitle, W / 2, y, { align: 'center' });
      y += 4;
    }
  }

  y += 3;
  doc.setDrawColor(ar, ag, ab);
  doc.setLineWidth(0.5);
  doc.line(M, y, W - M, y);
  y += 8;

  // ─── Titolo del PDF ───────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(pr, pg, pb);
  doc.text(
    input.target === 'takeaway' ? 'MENÙ ASPORTO' : input.target === 'aperitivo' ? 'APERITIVO & BAR' : 'MENÙ SALA',
    W / 2, y, { align: 'center' },
  );
  y += 9;

  const cats = filterCategoriesForTarget(input.categories, input.target);

  const renderCatHeader = (name: string, suffix?: string): void => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(INK[0], INK[1], INK[2]);
    doc.text(suffix ? `${name} ${suffix}` : name, M, y);
    doc.setDrawColor(ar, ag, ab);
    doc.setLineWidth(0.3);
    doc.line(M, y + 1.5, W - M, y + 1.5);
    y += 7;
  };

  for (const cat of cats) {
    const catItems = (input.target === 'takeaway' ? expandTakeawayPdfItems(input.items) : input.items)
      .filter((it) => it.category_id === cat.id && it.visible)
      .sort((a, b) => a.sort_order - b.sort_order);
    if (catItems.length === 0) continue;

    if (y > H - 35) {
      doc.addPage();
      y = M;
    }
    renderCatHeader(cat.name);

    for (const it of catItems) {
      // Calcolo preventivo altezza riga per evitare overflow
      const descLines = it.description
        ? (doc.splitTextToSize(it.description, innerW - 25) as string[])
        : [];
      const allergText = it.allergens.length > 0
        ? `Allergeni: ${it.allergens.map((a) => input.allergenLabels[a] ?? a).join(' · ')}`
        : null;
      const allergLines = allergText
        ? (doc.splitTextToSize(allergText, innerW - 25) as string[])
        : [];
      const rowHeight = 6 + descLines.length * 4 + allergLines.length * 3.5 + 3;

      if (y + rowHeight > H - M - 8 /* footer space */) {
        doc.addPage();
        y = M;
        renderCatHeader(cat.name, '(continua)');
      }

      // Nome + prezzo
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(INK[0], INK[1], INK[2]);
      const priceText = formatPrice(it.price_cents);
      const priceW = doc.getTextWidth(priceText);
      const nameMaxW = innerW - priceW - 4;
      const nameLines = doc.splitTextToSize(it.name, nameMaxW) as string[];
      nameLines.forEach((line, idx) => {
        doc.text(line, M, y + idx * 5);
      });
      doc.text(priceText, W - M, y, { align: 'right' });
      y += Math.max(nameLines.length * 5, 5);

      // Descrizione
      if (descLines.length > 0) {
        doc.setFont('times', 'italic');
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        for (const line of descLines) {
          doc.text(line, M, y);
          y += 4;
        }
      }

      // Allergeni
      if (allergLines.length > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(110, 110, 110);
        for (const line of allergLines) {
          doc.text(line, M, y);
          y += 3.5;
        }
      }

      y += 2.5;
    }

    y += 4;
  }

  // ─── Footer su ogni pagina ────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(140, 140, 140);
    doc.text(
      'Gli ingredienti possono variare per disponibilità di stagione. Per intolleranze o richieste, chiedete al personale di sala.',
      M, H - 8, { maxWidth: innerW - 20 },
    );
    doc.text(`${p} / ${pageCount}`, W - M, H - 8, { align: 'right' });
  }

  const arrayBuffer = doc.output('arraybuffer') as ArrayBuffer;
  // base64 senza prefisso data:
  const dataUri = doc.output('datauristring');
  const base64 = dataUri.split(',')[1] ?? '';
  return { base64, arrayBuffer };
}

export function expandTakeawayPdfItems(items: MenuPdfItem[]): MenuPdfItem[] {
  return items.flatMap((item) => {
    const variant = parseTakeawayVariant(item.price_unit);
    if (!variant) return [item];
    return [
      { ...item, name: `${item.name} (${variant.kind === 'family' ? 'normale' : 'panino'})` },
      { ...item, name: `${item.name} (${variant.kind === 'family' ? 'famiglia' : 'piadina'})`, price_cents: variant.cents, description: null, allergens: [] },
    ];
  });
}
